import type {SxProps, Theme} from "@mui/material";
import {alpha} from "@mui/material";
import type {SystemStyleObject} from "@mui/system";
import {MONO_FONT} from "~/style/fonts";
import type {ToolCallStatus} from "~/pages/chat-agent/components/AgentActivity/ToolCallRow";

// Colours come from the theme: the `atlas` palette holds the Mithra 3.0 Atlas run tokens.

export const sectionSx: SxProps<Theme> = {mb: 1.5, maxWidth: "100%"};

/** Fold toggle: leading chevron, muted until hovered. */
export const headerSx: SxProps<Theme> = {
    gap: 0.75,
    height: 28,
    pl: 0.5,
    pr: 1,
    ml: -0.5,
    mb: 0.5,
    maxWidth: "100%",
    borderRadius: 1,
    color: "atlas.muted",
    transition: "background-color 120ms",
    "&:hover": {bgcolor: "atlas.hoverBg", color: "atlas.ink2"},
    "&:focus-visible": {outline: "2px solid", outlineColor: theme => theme.palette.atlas.violet},
};

export const getChevronSx = (expanded: boolean): SxProps<Theme> => ({
    width: 16,
    height: 16,
    flexShrink: 0,
    transform: expanded ? "rotate(90deg)" : "none",
});

/** Light sweep across a title while the agent is working; static under reduced motion. */
const shimmerStyle: SystemStyleObject<Theme> = {
    background: theme => {
        const {muted, ink} = theme.palette.atlas;
        return `linear-gradient(90deg, ${muted} 25%, ${ink} 50%, ${muted} 75%)`;
    },
    backgroundSize: "200% 100%",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
    animation: "agent-activity-shimmer 1.4s linear infinite",
    "@keyframes agent-activity-shimmer": {
        to: {backgroundPosition: "-200% 0"},
    },
    "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
        background: "none",
        color: "atlas.ink",
    },
};

const titleSx: SystemStyleObject<Theme> = {
    fontWeight: 500,
    color: "atlas.ink2",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

export const getTitleSx = (isWorking: boolean): SxProps<Theme> =>
    isWorking ? {...titleSx, ...shimmerStyle} : titleSx;

/** Section body: a hairline rail on the left, scrolling once it gets tall. */
export const bodySx: SxProps<Theme> = {
    mb: 1,
    pl: 1.25,
    maxHeight: 320,
    overflowY: "auto",
    borderLeft: "1px solid",
    borderLeftColor: "atlas.line",
    color: "atlas.muted",
    typography: "body2",
    wordWrap: "break-word",
};

/** Thought markdown stays inside the chat column, as the Atlas answer bubble does: long code breaks, wide blocks scroll. */
export const thoughtMarkdownSx: SxProps<Theme> = {
    "& p": {my: 0.75},
    "& code": {overflowWrap: "break-word"},
    "& pre": {maxWidth: "100%", overflowX: "auto"},
    "& table": {display: "block", maxWidth: "100%", overflowX: "auto"},
};

export const toolRowSx: SxProps<Theme> = {py: 0.5};

export const toolNameSx: SxProps<Theme> = {fontFamily: MONO_FONT};

const markerSx: SystemStyleObject<Theme> = {
    width: 18,
    height: 18,
    flexShrink: 0,
    borderRadius: "50%",
};

/** Step marker: tinted check when done, violet ring while running, small dot when no response came. */
export const stepMarkerSx: Record<ToolCallStatus, SxProps<Theme>> = {
    done: {...markerSx, bgcolor: "atlas.goodBg", color: "atlas.goodInk"},
    running: markerSx,
    no_response: markerSx,
};

export const spinnerSx: SxProps<Theme> = {
    width: 14,
    height: 14,
    boxSizing: "border-box",
    borderRadius: "50%",
    border: "2px solid",
    borderColor: "atlas.line2",
    borderTopColor: "atlas.violet",
    animation: "agent-activity-spin 0.7s linear infinite",
    "@keyframes agent-activity-spin": {
        to: {transform: "rotate(360deg)"},
    },
    "@media (prefers-reduced-motion: reduce)": {animation: "none"},
};

export const waitDotSx: SxProps<Theme> = {width: 6, height: 6, borderRadius: "50%", bgcolor: "atlas.decor"};

export const stepLabelSx: Record<ToolCallStatus, SxProps<Theme>> = {
    done: {color: "atlas.ink2"},
    running: {color: "atlas.ink"},
    no_response: {color: "atlas.faint"},
};

/** Right-aligned time a finished step took. */
export const stepDurationSx: SxProps<Theme> = {
    ml: "auto",
    pl: 1,
    flexShrink: 0,
    fontFamily: MONO_FONT,
    fontVariantNumeric: "tabular-nums",
    color: "atlas.faint",
};

/** First row of an agent turn (a wrapping row Stack): avatar · title · status · time. */
export const turnHeadSx: SxProps<Theme> = {
    columnGap: 1,
    rowGap: 0.5,
    mb: 1.5,
};

/** Violet pulse around the agent's IconWrapper while it works. */
export const workingPulseSx: SxProps<Theme> = {
    animation: "agent-activity-pulse 1.6s ease-in-out infinite",
    "@keyframes agent-activity-pulse": {
        "0%, 100%": {boxShadow: theme => `0 0 0 0 ${alpha(theme.palette.atlas.violet, 0.22)}`},
        "50%": {boxShadow: theme => `0 0 0 6px ${alpha(theme.palette.atlas.violet, 0)}`},
    },
    "@media (prefers-reduced-motion: reduce)": {animation: "none"},
};

/** Green "complete" dot on the avatar's corner. */
export const doneBadgeSx: SxProps<Theme> = {
    "& .MuiBadge-dot": {
        width: 11,
        height: 11,
        minWidth: 11,
        boxSizing: "border-box",
        borderRadius: "50%",
        bgcolor: "atlas.goodInk",
        border: "2px solid",
        borderColor: "background.paper",
    },
};

export const turnTitleSx: SxProps<Theme> = {fontWeight: 600, color: "atlas.ink"};

export const workingTitleSx: SxProps<Theme> = {fontWeight: 600, ...shimmerStyle};

export type OutcomeTone = "good" | "info" | "error" | "plain";

/** Palette paths per tone: tinted background, coloured text. */
const TONES: Record<OutcomeTone, {bg: string; ink: string}> = {
    good: {bg: "atlas.goodBg", ink: "atlas.goodInk"},
    info: {bg: "atlas.violetBg", ink: "atlas.violetInk"},
    error: {bg: "atlas.errorBg", ink: "error.main"},
    plain: {bg: "atlas.bgRow", ink: "atlas.ink2"},
};

/** Outcome pill on the shared Tag: tinted background, coloured text, fully rounded. */
export const getOutcomeTagSx = (tone: OutcomeTone): SxProps<Theme> => ({
    "&&": {
        height: 24,
        borderRadius: "100px",
        bgcolor: TONES[tone].bg,
        color: TONES[tone].ink,
        fontWeight: 500,
    },
});

/** Live elapsed time at the end of the working head. */
export const elapsedTimerSx: SxProps<Theme> = {
    ml: "auto",
    minWidth: 44,
    textAlign: "right",
    fontFamily: MONO_FONT,
    fontVariantNumeric: "tabular-nums",
    color: "atlas.ink2",
};

/** Right-aligned "Today 15:06 · worked for 4s". */
export const turnMetaSx: SxProps<Theme> = {
    ml: "auto",
    color: "atlas.muted",
    fontVariantNumeric: "tabular-nums",
};

export const progressSx: SxProps<Theme> = {mt: 0.75, mb: 0.5, width: "100%", minWidth: 0};

export const progressBarSx: SxProps<Theme> = {height: 6, borderRadius: 1, bgcolor: "primary.100"};

export const progressCaptionSx: SxProps<Theme> = {display: "block", mt: 0.5};
